import { PocButton } from 'commons/components/components-wrapper/poc-button.component';
import { PocComponentFooter } from 'commons/components/components-wrapper/poc-component-footer.component';
import { PocComponentHeader } from 'commons/components/components-wrapper/poc-component-header.component';
import { PocIcon } from 'commons/components/components-wrapper/poc-icon.component';
import { PocProgressSpinner } from 'commons/components/components-wrapper/poc-progress-spinner.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import { useTranslation } from 'core/i18n/i18n';
import { SettingsCardLayout } from 'modules/settings/settings.style';
import React from 'react';
import { TrainingRegistrationManagementComponent } from './commons/training-registration-management.component';
import {
    TrainingRegistrationManagementSection,
    TrainingRegistrationSectionContent,
    TrainingRegistrationSectionContentHeader,
    TrainingRegistrationSectionHeader,
} from './settings-integrations-lms-api-integration-training-registration.style';
import { TrainingRegistration } from './types/lms-training-registration.type';

interface SettingsIntegrationsLmsApiIntegrationTrainingRegistrationProps {
    trainingRegistrations: TrainingRegistration[];
    loading?: boolean;
    onAddTrainingRegistration: VoidFunction;
    onRemoveTrainingRegistration: (trainingRegistration: TrainingRegistration) => void;
    onChangeTrainingRegistration: (trainingRegistration: TrainingRegistration) => void;
    onApplyTrainingRegistration: VoidFunction;
}

export const SettingsIntegrationsLmsApiIntegrationTrainingRegistration: React.FC<
    SettingsIntegrationsLmsApiIntegrationTrainingRegistrationProps
> = (props) => {
    const {
        trainingRegistrations,
        loading,
        onAddTrainingRegistration,
        onChangeTrainingRegistration,
        onRemoveTrainingRegistration,
        onApplyTrainingRegistration,
    } = props;

    const { t } = useTranslation();

    // To move outside with the bar action
    const areValidConfiguration = () => {
        const externalIds = trainingRegistrations.map((tr) => tr.externalId);
        const names = trainingRegistrations.map((tr) => tr.name);
        return (
            trainingRegistrations.length &&
            trainingRegistrations.every((tr) => tr.externalId && tr.name) &&
            new Set(externalIds).size === externalIds.length &&
            new Set(names).size === names.length
        );
    };

    return (
        <SettingsCardLayout interactive rounded>
            <PocComponentHeader slot="header">
                <PocTypography element="h6" variant="title6">
                    {t('settings.integrations.lms-api-integration.training-registration.title')}
                </PocTypography>
            </PocComponentHeader>
            <TrainingRegistrationManagementSection>
                <TrainingRegistrationSectionHeader>
                    <PocTypography element="p" variant="body1">
                        {t('settings.integrations.lms-api-integration.training-registration.description')}
                    </PocTypography>
                </TrainingRegistrationSectionHeader>
                {loading ? (
                    <div className="loading-mask">
                        <PocProgressSpinner size="xLarge" />
                    </div>
                ) : (
                    <TrainingRegistrationSectionContent>
                        <TrainingRegistrationSectionContentHeader>
                            <PocTypography element="span" variant="subtitle2" className="mandatory-fields">
                                {t('settings.integrations.lms-api-integration.training-registration.name.label')}
                            </PocTypography>
                            <PocTypography element="span" variant="subtitle2" className="mandatory-fields">
                                {t('settings.integrations.lms-api-integration.training-registration.external-id.label')}
                            </PocTypography>
                            <PocTypography element="span" variant="subtitle2" className="actions">
                                {t('settings.integrations.lms-api-integration.training-registration.active.label')}
                            </PocTypography>
                        </TrainingRegistrationSectionContentHeader>
                        {trainingRegistrations.map((tr) => (
                            <TrainingRegistrationManagementComponent
                                key={`training-registration-${tr.index}`}
                                trainingRegistration={tr}
                                trainingRegistrations={trainingRegistrations}
                                onChange={onChangeTrainingRegistration}
                                onRemove={onRemoveTrainingRegistration}
                            />
                        ))}
                    </TrainingRegistrationSectionContent>
                )}
                <PocButton variant="tertiary" onClick={onAddTrainingRegistration}>
                    <PocIcon slot="prefix" name="circle_add" family="outlined"></PocIcon>
                    {t('settings.integrations.lms-api-integration.training-registration.add')}
                </PocButton>
            </TrainingRegistrationManagementSection>
            <PocComponentFooter slot="footer">
                <div slot="suffix">
                    <PocButton
                        variant="secondary"
                        disabled={!areValidConfiguration()}
                        onClick={onApplyTrainingRegistration}
                    >
                        {t('settings.integrations.active-directory.data-points.button.apply')}
                    </PocButton>
                </div>
            </PocComponentFooter>
        </SettingsCardLayout>
    );
};
